import React, { FC, useEffect, useRef, useState } from "react"

import { HeartFilled, HeartOutlined, LoadingOutlined, PhoneOutlined, ExportOutlined, StopOutlined } from "@ant-design/icons"
import { Button, Collapse, Tooltip } from "antd"
import Table, { ColumnsType } from "antd/es/table"

import EditModal from "./EditModal"
import FilterTable from "./FilterTable"
import PhotoStatusButton from "./PhotoStatusButton"
import { SourceIcon } from "./SourceIcon"
import { IAdsListPayload } from "../../../../types/ads"
import { adsApi } from "../../../api/endpoints/adsApi"
import { errorTagColor, successTagColor } from "../../../constants/colors"
import { adminRole } from "../../../constants/roles"
import { useActions } from "../../../hooks/useActions"
import { useAllowedRoles } from "../../../hooks/useAllowedRoles"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { cleanObject } from "../../../utils/data"
import { playNewAdSound } from "../../../utils/sound"
import { showErrorNotification, showSuccessNotification } from "../../../utils/ui"
import AdsBanners from "../../ui/banners/AdsBanners"
import ColorTag from "../../ui/color-tag/ColorTag"
import DescriptionPopover from "../../ui/description-popover/DescriptionPopover"
import MetroView from "../../ui/metro-view/MetroView"
import { PhoneCell } from "../../ui/phone-cell/PhoneCell"
import PriceHistory from "../../ui/price-history/PriceHistory"

import "./AdsPage.scss"

interface ISourseProps {
    sourse: string
}

const SourceView: FC<ISourseProps> = ({ sourse }) => {
    switch (sourse) {
        case "Авито":
            return (

                <ColorTag
                    color={successTagColor}
                >
                    {sourse}
                </ColorTag>
            )
        case "ЦИАН":
            return (
                <ColorTag color="#1677ff">
                    {sourse}
                </ColorTag>
            )
        case "ЮЛА":
            return (
                <ColorTag color="#7092fe">
                    {sourse}
                </ColorTag>
            )

        default:
            return (
                <ColorTag
                    color="#ef961f"
                >
                    {sourse}
                </ColorTag>
            )
    }
}

interface IStatusProps {
    status: string
    id: number
    setEditOpen: (v: boolean) => void
    setEditId: (v: number) => void
    sendCall: (id: number) => void
}

const StatusView: FC<IStatusProps> = ({ status, id, setEditId, setEditOpen, sendCall }) => {
    const openEdit = () => {
        setEditId(id)
        setEditOpen(true)
    }

    switch (status) {
        case "Наша квартира":
            return (

                <ColorTag
                    color={successTagColor}
                    onClick={() => openEdit()}
                    pointer
                >
                    {status}
                </ColorTag>
            )
        case "Звонок":
            return (
                <ColorTag color="#2c78b2">
                    {status}
                </ColorTag>
            )
        case "Новое":
            return (
                <>
                    <ColorTag defaultColor>
                        {status}
                    </ColorTag>
                    <Button
                        type="primary"
                        size="small"
                        className="call-btn"
                        icon={<PhoneOutlined />}
                        onClick={() => sendCall(id)}
                    />
                </>
            )

        default:
            return (
                <>
                    <ColorTag
                        color={errorTagColor}
                        onClick={() => openEdit()}
                        pointer
                    >
                        {status}
                    </ColorTag>
                    <Button
                        type="primary"
                        size="small"
                        className="call-btn"
                        icon={<PhoneOutlined />}
                        onClick={() => sendCall(id)}
                    />
                </>
            )
    }
}

const AdsPage: FC = () => {
    let timeoutId: any
    const isAdmin = useAllowedRoles([ adminRole ])
    const [ pageSize, setPageSize ] = useState(10)
    const [ editOpen, setEditOpen ] = useState(false)
    const [ editId, setEditId ] = useState(0)
    
    // Для отслеживания новых объявлений
    const lastFirstItemId = useRef<string | number | null>(null)
    const isFirstLoad = useRef(true)

    const { isLoading, isFetching, tableData, tableMetaData, bannerData, tableFilters } = useTypedSelector((state) => state.ads)
    const { adsFetchData, adsAddToBlackList, setAdsFetching, adsSendCall, currentUserGetStatus } = useActions()

    const columns: ColumnsType<any> = [
        {
            title: "Дата",
            dataIndex: "date",
            key: "date",
            width: 130,
        },
        {
            title: "",
            dataIndex: "actions",
            key: "actions",
            width: 100,
            render: (_, record) => (
                <div className="actions-cell">
                    <PhotoStatusButton 
                        status={record.photo_status} 
                        onProcessPhoto={() => {
                            adsApi.processPhoto({ source_id: record.source.id, item_id: record.id.toString() })
                                .then((response) => {
                                    if (response.status === 200) {
                                        showSuccessNotification("Задача на обработку фото успешно поставлена.")
                                        setAdsFetching(true)
                                    }
                                })
                                .catch(() => {
                                    showErrorNotification("Ошибка при отправке данных на сервер")
                                })
                        }} 
                        onDownloadPhoto={() => {
                            adsApi.downloadPhoto({ source_id: record.source.id, item_id: record.id })
                                .catch(() => {
                                    showErrorNotification("Ошибка при скачивании")
                                })
                        }} 
                    />
                    <Tooltip title={record.favorites ? "Удалить из избранного" : "В избранное"}>
                        <Button
                            size="small"
                            type={record.favorites ? "primary" : "default"}
                            icon={record.favorites ? <HeartFilled /> : <HeartOutlined />}
                            style={record.favorites ? { background: '#ef4444', borderColor: '#ef4444' } : {}}
                            onClick={() => {
                                if (record.favorites) {
                                    adsApi.deleteFavoritesAd(record.id)
                                        .then((response) => {
                                            if (response.status === 200) {
                                                showSuccessNotification("Объявление удалено из избранного")
                                                setAdsFetching(true)
                                            }
                                        })
                                        .catch(() => {
                                            showErrorNotification("Ошибка при удалении из избранного, повторите попытку")
                                        })
                                } else {
                                    adsApi.addFavoritesAd(record.id)
                                        .then((response) => {
                                            if (response.status === 200) {
                                                showSuccessNotification("Объявление добавлено в избранное")
                                                setAdsFetching(true)
                                            }
                                        })
                                        .catch(() => {
                                            showErrorNotification("Ошибка при добавлении в избранное, повторите попытку")
                                        })
                                }
                            }}
                        />
                    </Tooltip>
                    <DescriptionPopover description={record.description} />
                    <Tooltip title="Открыть">
                        <a href={record.title?.link} target="_blank" rel="noopener noreferrer">
                            <Button size="small" icon={<ExportOutlined />} />
                        </a>
                    </Tooltip>
                </div>
            )
        },
        {
            title: "Заголовок",
            dataIndex: "title",
            key: "title",
            width: 320,
            render: (value, record) => (
                <div className="title-cell">
                    <span className="title-text">{value?.name}</span>
                    {record.duplicate_items?.rows?.length > 0 && (
                        <div className="duplicate-links">
                            {record.duplicate_items.rows.map((row: any, idx: number) => (
                                <a key={idx} href={row?.url} target="_blank" rel="noopener noreferrer">
                                    <Button size="small" icon={<SourceIcon sourceId={row?.source_id} />} />
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: "Источник",
            dataIndex: "source",
            key: "source",
            width: 100,
            render: (value, record) => value && (
                <div className="source-cell">
                    <SourceView sourse={value.name} />
                    {isAdmin && (
                        <Tooltip title="Добавить в чёрный список">
                            <Button
                                size="small"
                                className="blacklist-btn"
                                icon={<StopOutlined />}
                                onClick={() => {
                                    adsAddToBlackList(record.id, value.id, () => {
                                        showSuccessNotification("Объявление добавлено в ЧС")
                                    })
                                }}
                            >
                                В ЧС
                            </Button>
                        </Tooltip>
                    )}
                </div>
            )
        },
        {
            title: "Цена",
            dataIndex: "price",
            key: "price",
            width: 100,
            render: (value, record) => (
                <PriceHistory 
                    price={value} 
                    priceHistory={record.price_history} 
                />
            )
        },
        {
            title: "Адрес",
            dataIndex: "address",
            key: "address",
            width: 220,
            render: (value) => (
                <div className="address-cell">
                    <div className="address-name">{value?.name}</div>
                    {value?.metro?.name && (
                        <MetroView
                            title={value.metro.name}
                            color={`#${value.metro.color}`}
                            lineName={value.metro.line_name}
                            distance={value.metro.metro_distance}
                            walkTime={value.metro.metro_walk_time}
                        />
                    )}
                </div>
            )
        },
        {
            title: "Контакт",
            dataIndex: "phone",
            key: "phone",
            width: 120,
            render: (value, record) => (
                <PhoneCell isPaid={record?.is_paid} phoneUnavailable={record?.phone_unavailable}>
                    {value}
                </PhoneCell>
            ),
        },
        {
            title: "Оператор",
            dataIndex: "user",
            key: "user",
            width: 100,
        },
        {
            title: "Статус",
            dataIndex: "status",
            key: "status",
            width: 90,
            render: (value, record) => value && (
                <div className="status-cell">
                    <StatusView
                        status={value}
                        id={record.id}
                        setEditOpen={setEditOpen}
                        setEditId={setEditId}
                        sendCall={adsSendCall}
                    />
                </div>
            )
        },
    ]

    const fetchData = (paginationData = { page: 1, pageSize: 10, sorting: { name: "ASC" } }, withouLoading = false, onFinish?: () => void) => {
        const tempFilters = {
            data: (tableFilters.data?.start && tableFilters.data?.start) ? tableFilters.data : null,
            phone: tableFilters.phone || null,
            user_id: (tableFilters.user_id && tableFilters.user_id !== 0) ? tableFilters.user_id : null,
            status_id: (tableFilters.status_id && tableFilters.status_id !== 0) ? tableFilters.status_id : null,
            metro_id: (tableFilters.metro_id && tableFilters.metro_id !== 0) ? tableFilters.metro_id : null,
            source_id: (tableFilters.source_id && tableFilters.source_id !== 0) ? tableFilters.source_id : null,
            group_id: (tableFilters.group_id && tableFilters.group_id !== 0) ? tableFilters.group_id : null,
            room_id: (tableFilters.room_id && tableFilters.room_id !== 0) ? tableFilters.room_id : null,
            item_id: tableFilters.item_id || null,
            address: tableFilters.address || null,
            price_start: tableFilters.price_start || null,
            price_end: tableFilters.price_end || null
        }

        const data: IAdsListPayload = {
            page: paginationData.page,
            pageSize: paginationData.pageSize,
            sorting: paginationData.sorting,
            filters: cleanObject(tempFilters)
        }

        adsFetchData(data, withouLoading, onFinish)
    }

    const bindTableRequest = (paginationData = { page: 1, pageSize: pageSize, sorting: { name: "ASC" } }) => {
        fetchData(paginationData)

        if (timeoutId) {
            clearInterval(timeoutId)
        } else {
            for (let i = 0; i < 999999; i++) {
                clearInterval(i)
            }

            setInterval(() => {
                currentUserGetStatus()
            }, 2000)
        }

        timeoutId = setInterval(() => {
            fetchData(paginationData, true)
        }, 5000)
    }

    useEffect(() => {
        if (isFetching) {
            bindTableRequest()
            setAdsFetching(false)
        }
    }, [ isFetching ])

    // Отслеживаем появление новых объявлений
    useEffect(() => {
        if (tableData && tableData.length > 0) {
            const currentFirstId = tableData[0].id
            
            // Если это не первая загрузка и ID первого объявления изменился — новое объявление
            if (!isFirstLoad.current && 
                lastFirstItemId.current !== null && 
                currentFirstId !== lastFirstItemId.current) {
                playNewAdSound()
            }
            
            lastFirstItemId.current = currentFirstId
            isFirstLoad.current = false
        }
    }, [tableData])

    return (
        <div className="ads-page">
            <AdsBanners
                bannerData={bannerData}
                isLoading={isLoading}
            />

            <div className="ads-filter">
                <Collapse>
                    <Collapse.Panel header="Фильтр объявлений" key="1">
                        <FilterTable isLoading={isLoading} />
                    </Collapse.Panel>
                </Collapse>
            </div>

            <Table
                className="ads-table"
                dataSource={tableData || []}
                columns={columns}
                rowKey="id"
                scroll={{ x: 1100 }}
                rowClassName={(record) => {
                    if (record.removed) return "table__row_red"
                    if (record.raised_id === 2) return "table__row_raised"
                    return ""
                }}
                loading={{
                    spinning: isLoading,
                    indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />
                }}
                pagination={{
                    position: ["bottomLeft"],
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "25", "50", "100"],
                    defaultCurrent: 1,
                    defaultPageSize: 10,
                    current: tableMetaData?.currentPage || 1,
                    total: tableMetaData.total,
                    onChange: (page, per_page) => {
                        setPageSize(per_page)
                        bindTableRequest({
                            page: page,
                            pageSize: per_page,
                            sorting: { name: "ASC" }
                        })
                    },
                    onShowSizeChange: (page, per_page) => {
                        setPageSize(per_page)
                        bindTableRequest({
                            page: page,
                            pageSize: per_page,
                            sorting: { name: "ASC" }
                        })
                    },
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} из ${total} объявлений`,
                }}
                size="small"
            />

            <EditModal
                open={editOpen}
                setOpen={setEditOpen}
                id={editId}
            />
        </div>
    )
}

export default AdsPage
